import _ from 'lodash';
import moment from 'moment-timezone';
import Api from '../../../services/api';
import ClickThruService from '../../../services/clickthru.service';
import DataProvider from '../../../app/data-framework/data-provider';
import LoginService from "../../../services/login.service";

const DATE_FORMAT = 'YYYY/MM/DD'; // new API needs this format for start and end times
const LEGACY_DATE_FORMAT = 'YYYY-MM-DD'; // ClickThruService.clickThru requires this format

function translateData(columns, rows) {
  // Translate data into a more convenient format
  // TODO: More efficient way to do this? Here we are looping through the whole columns array for each row.
  let dataArray = [];
  for (let i = 0, _i = rows.length; i < _i; i++) {
    let dataMap = {};
    for (let j = 0, _j = columns.length; j < _j; j++) {
      dataMap[columns[j]] = rows[i][j];
    }
    dataArray.push(dataMap);
  }
  return dataArray;
}

function getEmailFilter(userEmail) {
  return {
    field: 'dimuser.email',
    operator: '=',
    value: userEmail,
    valueType: 'string'
  };
}

function getSystemTypeFilters(systemsList) {
  let filters = [];

  if (!systemsList || !systemsList.length) {
    systemsList = ['CISCOWEBEX', 'GMEET', 'MSTEAMS', 'ZOOM'];
  }

  for (let i = 0, _i = systemsList.length; i < _i; i++) {
    filters.push({
      field: 'dimsystem.systemtype',
      operator: '=',
      value: systemsList[i],
      valueType: 'string'
    });
  }

  return {
    conjunction: 'OR',
    filters: filters
  };
}

function getEmailAndSystemTypeFilter(userEmail, systemsList) {
  return {
    conjunction: 'AND',
    filters: [
      getEmailFilter(userEmail),
      getSystemTypeFilters(systemsList)
    ]
  };
}

function getBadQualityFilter(userEmail, systemsList) {
  return {
    conjunction: 'AND',
    filters: [
      getEmailFilter(userEmail),
      getSystemTypeFilters(systemsList),
      {
        field: 'dimqualityclass.qualityclass',
        operator: '=',
        value: 'bad',
        valueType: 'string'
      }
    ]
  };
}

function getTimeGrain(startTime, endTime) {
  let startMoment = moment(startTime, DATE_FORMAT),
      endMoment = moment(endTime, DATE_FORMAT),
      diff = endMoment.diff(startMoment, 'days');

  if (diff <= 60)  { return 'day'; }
  if (diff <= 180) { return 'week'; }
  if (diff > 180)  { return 'month'; }

  return 'day'; // default just in case something went wrong
}

function buildUserDataRequestParams(userId) {
  return {
    filter: {
      field: 'dimuser.dimusersid',
      operator: '=',
      value: userId,
      valueType: 'string'
    },
    dataset: 'userprofilexml'
  };
}

function buildConnectionQualityRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctparticipant.participantcount)',
    groupBy: 'dimqualityclass.qualityclass',
    start: startTime,
    end: endTime,
  };
}

function buildConnectionSpeedRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'avg(fctparticipant.avglinkspeed)',
    groupBy: 'dimconnectiontype.connectiontype',
    start: startTime,
    end: endTime
  };
}

function buildExperienceScoreRequestParams(userEmail, startTime, endTime) {
  let timeGrain = getTimeGrain(startTime, endTime);
  return {
    filter: getEmailFilter(userEmail),
    aggregateFact: 'avg(fctuserexperiencescore.experience_score)',
    timeGrain: timeGrain,
    sort: 'periodStartDate ASC',
    start: startTime,
    end: endTime
  };
}

function buildExperienceScoreKpiRequestParams(userEmail, startTime, endTime) {
  return {
    filter: getEmailFilter(userEmail),
    aggregateFact: 'avg(fctuserexperiencescore.experience_score)',
    start: startTime,
    end: endTime
  };
}

function buildMeetingDevicesRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctparticipant.participantcount)',
    groupBy: 'dimparticipant.device',
    sort: 'aggregateFact DESC',
    start: startTime,
    end: endTime
  };
}

function buildMeetingPeripheralsRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctuserperipheral.peripheralcount)',
    groupBy: 'dimuserperipheral.peripheral',
    sort: 'aggregateFact DESC',
    start: startTime,
    end: endTime
  };
}

function buildMeetingsAttendedRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctuser.attendedmeetingcount)',
    groupBy: 'dimsystem.systemtype',
    start: startTime,
    end: endTime
  };
}

function buildMeetingsHostedRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctuser.hostedmeetingcount)',
    groupBy: 'dimsystem.systemtype',
    start: startTime,
    end: endTime
  };
}

function buildNetworkTypeRequestParams(userEmail, startTime, endTime, systemsList) {
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctparticipant.participantcount)',
    groupBy: 'dimconnectiontype.connectiontype',
    start: startTime,
    end: endTime
  };
}

function buildUsageHistoryRequestParams(userEmail, startTime, endTime, systemsList) {
  let timeGrain = getTimeGrain(startTime, endTime);
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctuser.attendedmeetingcount)',
    timeGrain: timeGrain,
    sort: 'periodStartDate ASC',
    start: startTime,
    end: endTime
  };
}

function buildUsageMinutesRequestParams(userEmail, startTime, endTime, systemsList) {
  let timeGrain = getTimeGrain(startTime, endTime);
  return {
    filter: getEmailAndSystemTypeFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctparticipant.participantminute)',
    timeGrain: timeGrain,
    sort: 'periodStartDate ASC',
    start: startTime,
    end: endTime
  };
}

function buildUsageBadQualityRequestParams(userEmail, startTime, endTime, systemsList) {
  let timeGrain = getTimeGrain(startTime, endTime);
  return {
    filter: getBadQualityFilter(userEmail, systemsList),
    aggregateFact: 'sum(fctparticipant.meetingcount)',
    timeGrain: timeGrain,
    sort: 'periodStartDate ASC',
    start: startTime,
    end: endTime
  };
}

/* START LEGACY CLICKTHRU PARAMS */
// TODO: Since CPMA datasets are still using the old API query format, we have to construct a legacy query to utilize the clickthru service.
//       Once the datasets (and the clickthru service) are updated to use the new API, we should be able to eliminate these legacy queries.
function getLegacyEmailFilterCondition(userEmail, id = 1) {
  return {
    "hidden": false,
    "selectedRhs": [],
    "selectedLhsTitle": "User E-mail (Beta)",
    "selectedOperatorTitle": "equals",
    "rhsText": userEmail,
    "optitle": "equals",
    "conjunctionVisible": false,
    "valueType": "string",
    "selectedLhs": "dimuser.email",
    "selectedOperator": "=",
    "id": id,
    "listtype": false,
    "rhsCount": "",
    "desc": "User E-mail (Beta) equals " + userEmail
  };
}

function getLegacySystemTypeFilterCondition(systemType, id = 1) {
  return {
    "hidden": false,
    "selectedRhs": [],
    "selectedLhsTitle": "System Type (Acronym)",
    "selectedOperatorTitle": "equals",
    "rhsText": systemType,
    "optitle": "equals",
    "conjunctionVisible": false,
    "valueType": "string",
    "selectedLhs": "dimsystem.systemtype",
    "selectedOperator": "=",
    "id": id,
    "listtype": false,
    "rhsCount": "",
    "desc": "System Type (Acronym) equals " + systemType
  };
}

function getLegacyEmailAndSystemTypeFilters(userEmail, systemsList) {
  let systemTypeConditions = [];

  if (!systemsList || !systemsList.length) {
    systemsList = ['ciscowebex', 'gmeet', 'msteams', 'zoom'];
  }

  for(let i = 0, _i = systemsList.length; i < _i; i++) {
    systemTypeConditions.push(getLegacySystemTypeFilterCondition(systemsList[i], i+1));
  }

  return [{
      "selectedConjunction": "AND",
      "conditions": [
        getLegacyEmailFilterCondition(userEmail),
      ]
    },
    {
      "selectedConjunction": "OR",
      "conditions": systemTypeConditions
    }
  ];
}

function buildConnectionQualityLegacyWhereConditionObj(userEmail, filterVal, systemsList) {
  let filters = getLegacyEmailAndSystemTypeFilters(userEmail, systemsList);
  filters.push({
    "selectedConjunction": "AND",
    "conditions": [{
      "selectedLhs": "dimqualityclass.qualityclass",
      "selectedOperator": "=",
      "rhsText": filterVal,
      "selectedLhsTitle": "Quality",
      "valueType": "string"
    }]
  })
  return filters;
}

function buildMeetingDevicesLegacyWhereConditionObj(userEmail, filterVal, systemsList) {
   let filters = getLegacyEmailAndSystemTypeFilters(userEmail, systemsList);
   filters.push({
     "selectedConjunction": "AND",
     "conditions": [{
       "selectedLhs": "dimparticipant.device",
       "selectedOperator": "=",
       "rhsText": filterVal,
       "selectedLhsTitle": "Device",
       "valueType": "string"
     }]
   })
   return filters;
}

function buildMeetingPeripheralsLegacyWhereConditionObj(userEmail, filterVal, systemsList) {
  let filters = getLegacyEmailAndSystemTypeFilters(userEmail, systemsList);
  filters.push({
    "selectedConjunction": "OR",
    "conditions": [{
      "id": 1,
      "selectedLhs": "dimparticipant.camera",
      "selectedOperator": "=",
      "rhsText": filterVal,
      "rhsCount": "",
      "listtype": false,
      "valueType": "string",
      "selectedRhs": [],
      "conjunctionVisible": false,
      "hidden": false,
      "selectedLhsTitle": "Camera",
      "optitle": "equals",
      "desc": "Camera equals " + filterVal
    },{
      "id": 2,
      "selectedLhs": "dimparticipant.microphone",
      "selectedOperator": "=",
      "rhsText": filterVal,
      "rhsCount": "",
      "listtype": false,
      "valueType": "string",
      "selectedRhs": [],
      "conjunctionVisible": false,
      "hidden": false,
      "selectedLhsTitle": "Microphone",
      "optitle": "equals",
      "desc": "Microphone equals " + filterVal
    },{
      "id": 3,
      "selectedLhs": "dimparticipant.speaker",
      "selectedOperator": "=",
      "rhsText": filterVal,
      "rhsCount": "",
      "listtype": false,
      "valueType": "string",
      "selectedRhs": [],
      "conjunctionVisible": false,
      "hidden": false,
      "selectedLhsTitle": "Speaker",
      "optitle": "equals",
      "desc": "Speaker equals " + filterVal
    }]
  })
  return filters;
}

function buildMeetingsLegacyWhereConditionObj(userEmail, filterVal) {
  return [{
    "selectedConjunction": "AND",
    "conditions": [
      getLegacyEmailFilterCondition(userEmail, 1),
      getLegacySystemTypeFilterCondition(filterVal, 2)
    ]
  }]
}

function buildNetworkTypeLegacyWhereConditionObj(userEmail, filterVal, systemsList) {
  let filters = getLegacyEmailAndSystemTypeFilters(userEmail, systemsList);
  filters.push({
    "selectedConjunction": "AND",
    "conditions": [{
      "selectedLhs": "dimconnectiontype.connectiontype",
      "selectedOperator": "=",
      "rhsText": filterVal,
      "selectedLhsTitle": "Network Type",
      "valueType": "string"
    }]
  })
  return filters;
}

function buildQualityIssuesLegacyWhereConditionObj(userEmail, systemsList) {
  let filters = getLegacyEmailAndSystemTypeFilters(userEmail, systemsList);
  filters.push({
    "selectedConjunction": "AND",
    "conditions": [{
      "selectedLhs": "dimqualityclass.qualityclass",
      "selectedOperator": "=",
      "rhsText": "Bad",
      "selectedLhsTitle": "Quality",
      "valueType": "string"
    }]
  })
  return filters;
}
/* END LEGACY CLICKTHRU PARAMS */

function getFilterValueFromHighchartsEvent(event) {
  return (event.target && event.target.name) ? event.target.name :
         (event.point && event.point.name) ? event.point.name :
         (event.point && event.point.category) ? event.point.category :
         undefined
}

function doClickthru(type, metricColumn, category, whereConditionObj, filterValue, startTime, endTime) {
  let start = startTime.format(LEGACY_DATE_FORMAT), // start and end should be formatted like '2022-01-22'
      end = endTime.format(LEGACY_DATE_FORMAT),
      notin = false,
      date = undefined, // The 'date' param is not used in ClickThruService.clickThru()
      // We should be able to ignore groupColumn and groupColumns so that we don't have to deal with fetching metadata.
      // These params are used to add an additional filter group to the filter, but we can hard code the filter group instead.
      groupColumn = undefined,
      groupColumns = undefined;

  ClickThruService.clickThru(type, metricColumn, groupColumn, category, whereConditionObj, date, start, end, filterValue, notin, groupColumns);
}

export default class UserDetailsService {
  constructor() {}

  static getData(params) {
    return DataProvider.getInstance().fetchAssuranceDashboardServiceBaseURL().then(baseUrl => {
      return Api.request({
        url: baseUrl + '/data',
        method: 'POST',
        responseType: 'json',
        data: params
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Failed Request', error);
        throw error;
      });
    });
  }

  static getUserData(userId, startTime, endTime) {
    let params = {};
    params.userDataRequest = buildUserDataRequestParams(userId, startTime, endTime);
    return this.getData(params)
      .then(rawData => {
        let columns = rawData.userDataRequest.columns;
        let row = rawData.userDataRequest.rows;

         // we should only ever have 1 row for the user data request
        if (rawData.userDataRequest.rows.length > 1) {
          console.warn('Multiple records found for userId ' + userId + '. Expected only 1');
        }

        return translateData(columns, row);
      });
  }

  static getPanelsData(userEmail, startTime, endTime, systemsList) {
    let params = {};
    params.connectionQualityRequest = buildConnectionQualityRequestParams(userEmail, startTime, endTime, systemsList);
    params.connectionSpeedRequest = buildConnectionSpeedRequestParams(userEmail, startTime, endTime, systemsList)
    params.meetingDevicesRequest = buildMeetingDevicesRequestParams(userEmail, startTime, endTime, systemsList);
    params.meetingPeripheralsRequest = buildMeetingPeripheralsRequestParams(userEmail, startTime, endTime, systemsList);
    params.meetingsAttendedRequest = buildMeetingsAttendedRequestParams(userEmail, startTime, endTime, systemsList);
    params.meetingsHostedRequest = buildMeetingsHostedRequestParams(userEmail, startTime, endTime, systemsList);
    params.networkTypeRequest = buildNetworkTypeRequestParams(userEmail, startTime, endTime, systemsList);
    params.usageHistoryRequest = buildUsageHistoryRequestParams(userEmail, startTime, endTime, systemsList);
    params.usageMinutesRequest = buildUsageMinutesRequestParams(userEmail, startTime, endTime, systemsList);
    params.usageBadQualityRequest = buildUsageBadQualityRequestParams(userEmail, startTime, endTime, systemsList);
    params.userExperienceScoreRequest = buildExperienceScoreRequestParams(userEmail, startTime, endTime);
    params.userExperienceScoreKpiRequest = buildExperienceScoreKpiRequestParams(userEmail, startTime, endTime);

    return this.getData(params)
      .then(rawData => {
        let result = {},
            connectionQualityColumns = rawData.connectionQualityRequest.columns,
            connectionQualityRows = rawData.connectionQualityRequest.rows,
            connectionSpeedColumns = rawData.connectionSpeedRequest.columns,
            connectionSpeedRows = rawData.connectionSpeedRequest.rows,
            meetingDevicesColumns = rawData.meetingDevicesRequest.columns,
            meetingDevicesRows = rawData.meetingDevicesRequest.rows,
            meetingPeripheralsColumns = rawData.meetingPeripheralsRequest.columns,
            meetingPeripheralsRows = rawData.meetingPeripheralsRequest.rows,
            meetingsAttendedColumns = rawData.meetingsAttendedRequest.columns,
            meetingsAttendedRows = rawData.meetingsAttendedRequest.rows,
            meetingsHostedColumns = rawData.meetingsHostedRequest.columns,
            meetingsHostedRows = rawData.meetingsHostedRequest.rows,
            networkTypeColumns = rawData.networkTypeRequest.columns,
            networkTypeRows = rawData.networkTypeRequest.rows,
            usageHistoryColumns = rawData.usageHistoryRequest.columns,
            usageHistoryRows = rawData.usageHistoryRequest.rows,
            usageMinutesColumns = rawData.usageMinutesRequest.columns,
            usageMinutesRows = rawData.usageMinutesRequest.rows,
            usageBadQualityColumns = rawData.usageBadQualityRequest.columns,
            usageBadQualityRows = rawData.usageBadQualityRequest.rows,
            userExperienceScoreColumns = rawData.userExperienceScoreRequest.columns,
            userExperienceScoreRows = rawData.userExperienceScoreRequest.rows,
            userExperienceScoreKpiColumns = rawData.userExperienceScoreKpiRequest.columns,
            userExperienceScoreKpiRows = rawData.userExperienceScoreKpiRequest.rows;

        result.connectionQuality = translateData(connectionQualityColumns, connectionQualityRows);
        result.connectionSpeed = translateData(connectionSpeedColumns, connectionSpeedRows);
        result.meetingDevices = translateData(meetingDevicesColumns, meetingDevicesRows);
        result.meetingPeripherals = translateData(meetingPeripheralsColumns, meetingPeripheralsRows);
        result.meetingsAttended = translateData(meetingsAttendedColumns, meetingsAttendedRows);
        result.meetingsHosted = translateData(meetingsHostedColumns, meetingsHostedRows);
        result.networkType = translateData(networkTypeColumns, networkTypeRows);
        result.usageHistory = translateData(usageHistoryColumns, usageHistoryRows);
        result.usageMinutes = translateData(usageMinutesColumns, usageMinutesRows);
        result.usageBadQuality = translateData(usageBadQualityColumns, usageBadQualityRows);
        result.userExperienceScore = translateData(userExperienceScoreColumns, userExperienceScoreRows);
        result.userExperienceScoreKpi = translateData(userExperienceScoreKpiColumns, userExperienceScoreKpiRows);

        return result;
      })
  }

  static connectionQualityClickCallback(userEmail, startTime, endTime, highchartsEvent, systemsList) {
    let type = 'Pie',
        metricColumn = 'fctparticipant.participantcount',
        category = 'participant',
        filterValue = getFilterValueFromHighchartsEvent(highchartsEvent),
        whereConditionObj = buildConnectionQualityLegacyWhereConditionObj(userEmail, filterValue, systemsList);

    doClickthru(type, metricColumn, category, whereConditionObj, filterValue, startTime, endTime);
  }

  static meetingDevicesClickCallback(userEmail, startTime, endTime, highchartsEvent, systemsList) {
    let type = 'Trend',
        metricColumn = 'fctparticipant.participantcount',
        category = 'participant',
        filterValue = getFilterValueFromHighchartsEvent(highchartsEvent),
        whereConditionObj = buildMeetingDevicesLegacyWhereConditionObj(userEmail, filterValue, systemsList);

    doClickthru(type, metricColumn, category, whereConditionObj, filterValue, startTime, endTime);
  }

  static meetingPeripheralsClickCallback(userEmail, startTime, endTime, highchartsEvent, systemsList) {
    let type = 'Trend',
        metricColumn = 'fctuserperipheral.peripheralcount',
        category = 'participant',
        filterValue = getFilterValueFromHighchartsEvent(highchartsEvent),
        whereConditionObj = buildMeetingPeripheralsLegacyWhereConditionObj(userEmail, filterValue, systemsList);

    doClickthru(type, metricColumn, category, whereConditionObj, filterValue, startTime, endTime);
  }

  static meetingsClickCallback(userEmail, startTime, endTime, highchartsEvent) {
    // highchartsEvent.point.category contains the system type (e.g. 'MSTEAMS')
    // highchartsEvent.point.series.name contains the meeting type ('Attended' vs 'Hosted')
    let metricColumn,
        category,
        type = 'Trend',
        filterValue = getFilterValueFromHighchartsEvent(highchartsEvent),
        series = (highchartsEvent.point && highchartsEvent.point.series) ? highchartsEvent.point.series.name : undefined,
        whereConditionObj = buildMeetingsLegacyWhereConditionObj(userEmail, filterValue);

    if (series.toLowerCase() === 'attended') {
      metricColumn = 'fctuser.attendedmeetingcount';
      category = 'participant';
    } else if (series.toLowerCase() === 'hosted') {
      metricColumn = 'fctuser.hostedmeetingcount';
      category = 'meeting';
    } else {
      // something went wrong
      throw new Error('ClickThru failed -- Unknown or missing series name: ' + series);
    }

    doClickthru(type, metricColumn, category, whereConditionObj, filterValue, startTime, endTime);
  }

  static networkTypeClickCallback(userEmail, startTime, endTime, highchartsEvent, systemsList) {
    let type = 'Pie',
        metricColumn = 'fctparticipant.participantcount',
        category = 'participant',
        filterValue = getFilterValueFromHighchartsEvent(highchartsEvent),
        whereConditionObj = buildNetworkTypeLegacyWhereConditionObj(userEmail, filterValue, systemsList);

    doClickthru(type, metricColumn, category, whereConditionObj, filterValue, startTime, endTime);
  }

  static usageHistoryClickCallback(userEmail, startTime, endTime, highchartsEvent, systemsList) {
    let type = 'Trend',
        metricColumn = 'fctparticipant.participantcount',
        category = 'user',
        filterValue = undefined, // this clickthru only requires start and end times
        whereConditionObj = getLegacyEmailAndSystemTypeFilters(userEmail, systemsList),
        timeGrain = getTimeGrain(startTime, endTime) + 's',
        newStartTime,
        newEndTime;

    if (_.get(highchartsEvent, 'point.series.name', 'not found').toLowerCase() === 'quality issues') {
      whereConditionObj = buildQualityIssuesLegacyWhereConditionObj(userEmail, systemsList)
    }

    // copied/modified from flexipanel-widget.js
    if (highchartsEvent.point.x && (moment(highchartsEvent.point.x).isValid()) && (moment(highchartsEvent.point.x).isAfter('2000-01-01T00:00:00Z'))) {
      newStartTime = moment(highchartsEvent.point.x).startOf('day');
      newEndTime = moment(highchartsEvent.point.x).endOf('day').add(1, timeGrain).subtract(1, 'days');
    }

    doClickthru(type, metricColumn, category, whereConditionObj, filterValue, newStartTime, newEndTime);
  }

  static getCurrentMoment() {
    return moment.tz(_.get(LoginService.userObject, 'timezoneId', ''));
  }

  static getTimezoneText() {
    return _.get(LoginService.userObject, 'timezoneDisplay', '');
  }

  static getDateFormat() {
    return DATE_FORMAT;
  }

  static getDefaultDateRange() {
    return {
      startDate: this.getCurrentMoment().subtract(1, 'month').startOf('day'),
      endDate: this.getCurrentMoment().endOf('day')
    }
  }
}
